import { cva, type VariantProps } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Icon, type IconName } from '../../atoms/icon/icon'

/**
 * The Side menu set (210:16813): Collapsed = False (210:16812), 220px wide with
 * icon and label, and Collapsed = True (210:16811), the icons alone at their
 * natural 72.25px. The board draws the menu at 1.0035 scale, so its paddings,
 * item height and radius are their own `atlas-menu-*` tokens, and its labels are
 * Roboto Regular 16.056, not a Mona Sans style.
 *
 * Collapsed hides the nested items, as drawn. The footer is 72px expanded and
 * 64.223px collapsed, also as drawn.
 *
 * The 1px right Stroke sits inside the frame in the Figma, so it is drawn by an
 * `after:` rule over the edge rather than a border: the collapsed menu stays at its
 * drawn 72.25px instead of growing by the stroke.
 */
export const sideMenuVariants = cva(
	'relative flex h-full flex-col justify-between border-t-(length:--atlas-border-width-menu) border-atlas-stroke bg-atlas-light font-atlas-sans after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-atlas-stroke',
	{
		variants: {
			collapsed: {
				false: 'w-55',
				true: 'w-fit',
			},
		},
		defaultVariants: { collapsed: false },
	},
)

// The Figma draws no current-page look: the current item is only `aria-current`.
// The focus outline is not drawn in the Figma either; it is the kit's keyboard focus.
const itemClass = cn(
	'flex h-atlas-menu-item-height cursor-pointer items-center gap-atlas-menu-item-x rounded-atlas-menu-item bg-atlas-light px-atlas-menu-item-x font-atlas-roboto text-atlas-menu-item',
	focusRing,
)

// Item text as the component binds it (210:16812): a top-level item with no nested
// items ("Overview") is Dark, a group heading ("Work Orders") and its nested items
// are Black. Logout is Dark too.
function itemTextClass(item: SideMenuItem, nested: boolean): string {
	return nested || (item.children?.length ?? 0) > 0 ? 'text-atlas-black' : 'text-atlas-dark'
}

/** One entry of the side menu; `children` are its nested items (one level, as drawn). */
export interface SideMenuItem {
	id: string
	label: string
	/**
	 * The 24px glyph. The Figma's nested items reference `global` and
	 * `notification-03`, both exported as empty groups, so a nested item without
	 * an icon keeps the drawn empty 24px slot.
	 */
	icon?: IconName
	/** Where the item links to; the screen's router handles it (react-aria `RouterProvider`). */
	href: string
	children?: SideMenuItem[]
}

export interface SideMenuProps
	extends Omit<HTMLAttributes<HTMLElement>, 'children'>,
		VariantProps<typeof sideMenuVariants> {
	items: SideMenuItem[]
	/** The id of the item for the current page, marked with `aria-current="page"`. */
	activeId?: string
	/** Called with the item's id when it is pressed. */
	onNavigate?: (id: string) => void
	/** Called when Logout is pressed. */
	onLogout?: () => void
	logoutLabel?: string
}

interface MenuLinkProps {
	item: SideMenuItem
	/** Whether the item is a nested item, which sets its text colour. */
	nested?: boolean
	collapsed: boolean
	activeId?: string
	onNavigate?: (id: string) => void
}

function MenuLink({ item, nested = false, collapsed, activeId, onNavigate }: MenuLinkProps) {
	return (
		<AriaLink
			href={item.href}
			aria-label={collapsed ? item.label : undefined}
			aria-current={item.id === activeId ? 'page' : undefined}
			onPress={() => onNavigate?.(item.id)}
			className={cn(itemClass, itemTextClass(item, nested), !collapsed && 'w-full')}
		>
			{item.icon ? <Icon name={item.icon} /> : <span aria-hidden="true" className="size-atlas-menu-icon shrink-0" />}
			{collapsed ? null : <span className="min-w-0 flex-1 truncate">{item.label}</span>}
		</AriaLink>
	)
}

function MenuEntry(props: MenuLinkProps) {
	const { item, collapsed } = props
	const nested = collapsed ? [] : (item.children ?? [])
	return (
		<li className={cn('flex flex-col gap-1', !collapsed && 'w-full')}>
			<MenuLink {...props} />
			{nested.length > 0 ? (
				<ul className="flex w-full flex-col">
					{nested.map((child) => (
						<li key={child.id} className="w-full">
							<MenuLink {...props} item={child} nested />
						</li>
					))}
				</ul>
			) : null}
		</li>
	)
}

/**
 * The app's primary navigation: a `nav` landmark with the menu items on top and
 * Logout in the footer. Collapsing is controlled by the screen; the Figma draws no
 * control for it. Collapsed items keep their label as the accessible name.
 */
export function SideMenu({
	items,
	collapsed,
	activeId,
	onNavigate,
	onLogout,
	logoutLabel = 'Logout',
	className,
	'aria-label': ariaLabel = 'Main',
	...props
}: SideMenuProps) {
	const isCollapsed = Boolean(collapsed)
	return (
		<nav {...props} aria-label={ariaLabel} className={cn(sideMenuVariants({ collapsed: isCollapsed }), className)}>
			<ul className={cn('flex flex-col gap-3 px-atlas-menu-inset-x py-atlas-menu-inset-y', !isCollapsed && 'w-full')}>
				{items.map((item) => (
					<MenuEntry key={item.id} item={item} collapsed={isCollapsed} activeId={activeId} onNavigate={onNavigate} />
				))}
			</ul>
			<div
				className={cn(
					'flex w-full shrink-0 items-center border-t-(length:--atlas-border-width-menu) border-atlas-stroke px-atlas-menu-inset-x',
					isCollapsed ? 'h-atlas-menu-footer-collapsed' : 'h-18',
				)}
			>
				<AriaButton
					onPress={onLogout}
					aria-label={isCollapsed ? logoutLabel : undefined}
					className={cn(
						'flex flex-1 cursor-pointer items-center gap-atlas-menu-item-x rounded-atlas-menu-item px-atlas-menu-item-x py-atlas-menu-logout-y text-left font-atlas-roboto text-atlas-menu-item text-atlas-dark',
						focusRing,
					)}
				>
					<Icon name="logout-05" />
					{isCollapsed ? null : <span className="min-w-0 flex-1 truncate">{logoutLabel}</span>}
				</AriaButton>
			</div>
		</nav>
	)
}
